/**
 * Performance logging utility for tracking application load times
 * 
 * Usage:
 * 1. Call initializePerfLog() at app startup
 * 2. Enable with ?isPerfLogEnabled=true in URL
 * 3. Import and use setPerfLog with constants:
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * 
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 * 
 * // View results:
 * console.log(window.perfLog);
 * 
 * Design:
 * - setPerfLog() stores data in a flat Map (O(1) operation)
 * - Duplicate calls with the same key+type are silently discarded
 * - window.perfLog getter does ALL the work:
 *   - Builds the hierarchical tree from the flat map
 *   - Applies parent-child relationships based on PERF_PATH_MAP
 *   - Sorts nodes by time
 *   - Returns the complete structured tree
 * - This keeps the hot path (setPerfLog) extremely fast
 */

import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { PERF_EVENT,PERF_KEYS, PERF_PATH_MAP } from './constants';
import { captureResourceTimings, findOrCreateNode, sortPerfNodes } from './helpers';
import { PerfEventType, PerfKeyType,PerfNode } from './types';

// Re-export types and constants for convenience
export type { PerfNode, ResourceTiming, ResourceMatcher, PerfEventType, PerfKeyType } from './types';
export { PERF_KEYS, PERF_EVENT } from './constants';

// ==================== Module State ====================

// Flat storage: key -> { START?, COMPLETE? }
// All tree building is deferred to the getter
interface PerfLogEntry {
    START?: number;
    COMPLETE?: number;
}

const perfLogMap = new Map<PerfKeyType, PerfLogEntry>();
const URL_PARAM_IS_PERF_LOG_ENABLED = 'isPerfLogEnabled';

// Flag to track whether resource timings have been captured from Performance API
let resourceTimingsCaptured = false;

/**
 * Logs a performance event - JUST stores in flat map (O(1))
 * @param key - The event key (must be one of PERF_KEYS constants)
 * @param type - Event type: PERF_EVENT.START or PERF_EVENT.COMPLETE
 * @param val - Optional explicit timestamp value (defaults to performance.now())
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * setPerfLog(PERF_KEYS.APP_INIT_START, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 */
export const setPerfLog = (key: PerfKeyType, type: PerfEventType, val?: number) => {
    const queryParams = getQueryParamsAndQueryString().queryParams;
    if (queryParams[URL_PARAM_IS_PERF_LOG_ENABLED] !== 'true') {
        return;
    }
    // Ignore if key is not in PERF_PATH_MAP
    if (!(key in PERF_PATH_MAP)) {
        return;
    }

    let entry = perfLogMap.get(key);
    if (!entry) {
        entry = {};
        perfLogMap.set(key, entry);
    }

    // Only set value if not already set (discard duplicate updates)
    if (entry[type] === undefined) {
        entry[type] = val ?? performance.now();
        
        // Also set performance marker only for first occurrence
        try {
            const markerName = `${key}--${type}`;
            performance.mark(markerName);
        } catch (e) {
            // Silently fail if performance API is not available
        }
    }
    // If value already exists, silently discard this update
};

/**
 * Builds the hierarchical tree from the flat perfLogMap
 * This does ALL the work: tree building, parent-child relationships, sorting
 * Called only when window.perfLog is accessed
 */
const buildTreeFromMap = (): PerfNode[] => {
    const tree: PerfNode[] = [];
    
    // Iterate through all logged entries in the flat map
    Array.from(perfLogMap.entries()).forEach(([key, entry]) => {
        // Resolve the parent path for this key from PERF_PATH_MAP
        const parentPath = PERF_PATH_MAP[key as PerfKeyType];
        const fullPathParts = parentPath ? parentPath.split(".").concat(key) : [key];
        
        // Find or create the node in the tree (builds parent-child structure)
        const node = findOrCreateNode(tree, fullPathParts);
        
        // Copy the START and COMPLETE values to the node, converting to seconds
        if (entry.START !== undefined) {
            node.START = parseFloat((entry.START / 1000).toFixed(3));
        }
        if (entry.COMPLETE !== undefined) {
            node.COMPLETE = parseFloat((entry.COMPLETE / 1000).toFixed(3));
        }
    });
    
    return tree;
};

// ==================== Public API ====================

/**
 * Initializes the performance logging system
 * Only active when isPerfLogEnabled=true is in the URL
 * Uses Object.defineProperty getter to build and return the tree on-demand
 */
export const initializePerfLog = () => {
    const queryParams = getQueryParamsAndQueryString().queryParams;
    if (queryParams[URL_PARAM_IS_PERF_LOG_ENABLED] === 'true') {
        // Define perfLog as a property with a getter that builds the tree on-demand
        Object.defineProperty(window, 'perfLog', {
            get() {
                // Build the tree from the flat map (all tree logic happens here)
                const tree = buildTreeFromMap();
                
                // Capture resource timings on first access
                if (!resourceTimingsCaptured) {
                    resourceTimingsCaptured = true;
                    const resourceTimings = captureResourceTimings();
                    
                    // Add resource timings as top-level PerfNode objects to the tree
                    Object.entries(resourceTimings).forEach(([key, timing]) => {
                        tree.push({
                            key,
                            START: timing.start,
                            COMPLETE: timing.complete,
                            steps: [],
                        });
                    });
                }
                
                // Sort the tree by START time (fallback to COMPLETE time)
                // All transformation happens here in the getter
                return sortPerfNodes(tree);
            },
            configurable: true,
        });
        
        // Handle docParseEnd from perfMeta if available
        if ((window as any).perfMeta?.docParseEnd) {
            setPerfLog('DOCUMENT_PARSE', 'COMPLETE', (window as any).perfMeta.docParseEnd);
        }
    }
};
